import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Sparkles, Download, Image as ImageIcon, Share2, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function LogoStudio() {
  const [items, setItems] = useState([]);
  const [style, setStyle] = useState("modern minimalist");
  const [color, setColor] = useState("");
  const [generating, setGenerating] = useState(false);
  const [shareModal, setShareModal] = useState(null);

  const load = async () => {
    const { data } = await api.get("/ai/logos");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const token = localStorage.getItem("token");

  const imgSrc = (it) => {
    if (it.storage_path) return `${API}/files/${it.storage_path}?auth=${encodeURIComponent(token)}`;
    if (it.image_b64) return `data:image/png;base64,${it.image_b64}`;
    return "";
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/logo", { style, color_hint: color || undefined });
      setItems([data, ...items]);
      toast.success("Logo generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const download = async (it) => {
    try {
      const res = await fetch(imgSrc(it));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `logo-${it.id}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const share = async (it) => {
    try {
      const { data } = await api.post(`/ai/logo/${it.id}/share`);
      const url = `${window.location.origin}/share/logo/${data.slug}`;
      setShareModal({ url, slug: data.slug });
      navigator.clipboard.writeText(url);
      toast.success("Share link created & copied");
    } catch {
      toast.error("Could not create share link");
    }
  };

  return (
    <div className="space-y-8" data-testid="logo-studio-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Logo Studio</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Logos in seconds.</h1>
        <p className="text-white/50 mt-2 max-w-2xl">
          AI generates premium, scalable logo concepts tuned to your brand. Iterate until you love one.
        </p>
      </header>

      <div className="glass rounded-2xl p-6 lg:p-8">
        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Style</label>
            <select
              value={style}
              data-testid="logo-style-select"
              onChange={(e) => setStyle(e.target.value)}
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
            >
              <option>modern minimalist</option>
              <option>luxury monogram</option>
              <option>playful illustrated</option>
              <option>bold geometric</option>
              <option>elegant serif wordmark</option>
              <option>vintage badge</option>
              <option>tech futuristic</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Color hint (optional)</label>
            <input
              value={color}
              data-testid="logo-color-input"
              onChange={(e) => setColor(e.target.value)}
              placeholder="e.g. forest green, deep navy, #F97316"
              className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={generate}
              data-testid="logo-generate-button"
              disabled={generating}
              className="w-full inline-flex items-center justify-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" />
              {generating ? "Generating…" : "Generate logo"}
            </button>
          </div>
        </div>
      </div>

      <section>
        <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Your logos</div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">
            <ImageIcon className="h-8 w-8 mx-auto mb-3 text-white/30" />
            No logos yet. Generate your first one above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it) => (
              <div key={it.id} className="glass rounded-2xl overflow-hidden group" data-testid={`logo-item-${it.id}`}>
                <div className="aspect-square bg-white relative">
                  {(it.image_b64 || it.storage_path) ? (
                    <img src={imgSrc(it)} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-black/30">No image</div>
                  )}
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div className="text-xs text-white/50 font-mono">{it.style}</div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => share(it)}
                      data-testid={`logo-share-${it.id}`}
                      title="Create viral share link"
                      className="p-2 rounded-lg hover:bg-white/5"
                    >
                      <Share2 className="h-4 w-4 text-orange-400" />
                    </button>
                    <button
                      onClick={() => download(it)}
                      data-testid={`logo-download-${it.id}`}
                      title="Download"
                      className="p-2 rounded-lg hover:bg-white/5"
                    >
                      <Download className="h-4 w-4 text-white/60" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {shareModal && (
        <div
          className="fixed inset-0 z-50 grid place-items-center p-6 bg-black/70"
          onClick={() => setShareModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-2xl p-8 max-w-md w-full"
            data-testid="share-modal"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Your share page is live</div>
            <h3 className="font-heading text-2xl font-black mb-4">Spread the word</h3>
            <div className="glass rounded-lg p-3 font-mono text-xs text-white/70 break-all" data-testid="share-modal-url">
              {shareModal.url}
            </div>
            <div className="mt-5 flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareModal.url);
                  toast.success("Copied");
                }}
                data-testid="share-modal-copy"
                className="flex-1 glass px-4 py-2.5 rounded-lg text-sm hover:bg-white/5 inline-flex items-center justify-center gap-2"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
              <a
                href={shareModal.url}
                target="_blank"
                rel="noreferrer"
                data-testid="share-modal-open"
                className="flex-1 brand-gradient px-4 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90"
              >
                Open <ExternalLink className="h-4 w-4" />
              </a>
            </div>
            <button
              onClick={() => setShareModal(null)}
              className="mt-4 w-full text-xs text-white/50 hover:text-white"
              data-testid="share-modal-close"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
